import type {
  ClassificationArtifact,
  ClassificationOverrideEvent,
  CostTelemetryBudgetSummary,
  CostTelemetryCurrencyAmount,
  CostTelemetryPricingBasis,
  CostTelemetryRollup,
  EngineChainSummary,
  EngineFeedRequirementRow,
  ManifestArtifact,
  RequirementCriticArtifact,
  RequirementRegisterArtifact,
  TenderDocumentRegistryRow,
  TenderPack,
  TenderProgressEvent,
  TenderTakeoffAdapterArtifact,
  TenderTakeoffArtifact,
  TenderTakeoffReviewEvent,
  Requirement,
  SourceRef
} from "@automatry/schemas";
import { getApprovedLlmModelEntry } from "@automatry/schemas";
import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex } from "@noble/hashes/utils.js";

import { withAuthHeaders } from "./browser/apiAuthHeaders";
import type { AssistantTurnEvent } from "./features/assistant/types";

// Live provider ids are intentionally open-ended. Runtime capability fields,
// not this display identifier, decide whether web features are available.
export type LiveProviderMode = string;

export interface LiveProviderSummary {
  mode: LiveProviderMode;
  model: string;
  available: boolean;
  assistantId?: string;
  unavailableReason?: string;
  degradedReason?: string;
}

export interface LivePathStatus {
  requestedProvider: string;
  providerMode: LiveProviderMode;
  model: string;
  enabled: boolean;
  configured: boolean;
  available: boolean;
  readiness: "ready" | "disabled" | "unavailable";
  noMockFallback: boolean;
  unavailableReason?: string;
  disabledReason?: string;
  degradedReason?: string;
}

export interface TenderIntakeRuntimeStatus {
  enabled: boolean;
  readiness: "ready" | "disabled";
  mode: "local" | "hosted";
  uploadBucketConfigured: boolean;
  workerQueueConfigured: boolean;
  message?: string;
  missing: string[];
}

export interface LiveHealthResponse {
  ok: boolean;
  provider: LiveProviderMode;
  requestedProvider: string;
  providerAvailable: boolean;
  model: string;
  unavailableReason?: string;
  degradedReason?: string;
  llmSettings?: {
    provider: string;
    modelId: string;
    revision: number;
    updatedAt: string | null;
    source: string;
    invalidReason?: string;
  };
  livePath: LivePathStatus;
  tenderIntake?: TenderIntakeRuntimeStatus;
  liveSpec?: {
    admission?: {
      defaultMaxApartmentTypes: number;
      hardMaxApartmentTypes: number;
      configuredMaxApartmentTypes: number;
      effectiveMaxApartmentTypes: number;
      envVar?: string;
      requestField?: string;
    };
  };
  endpoints: string[];
}

export type LiveHealthCapabilityState =
  | "ready"
  | "offline"
  | "disabled"
  | "unavailable";

/**
 * Classify health solely from capability signals. Any future live provider
 * that reports the same ready contract works without a web release.
 */
export function liveHealthCapabilityState(
  health: LiveHealthResponse
): LiveHealthCapabilityState {
  if (!health.ok) {
    return "unavailable";
  }

  if (
    health.providerAvailable &&
    health.livePath.available &&
    health.livePath.readiness === "ready"
  ) {
    return "ready";
  }

  if (health.livePath.readiness === "disabled") {
    return health.providerAvailable ? "offline" : "disabled";
  }

  return "unavailable";
}

export function liveProviderDisplayName(provider: string): string {
  const normalized = provider.trim().toLowerCase();

  if (!normalized || normalized === "disabled" || normalized === "mock") {
    return "AI";
  }

  return normalized
    .split(/[-_\s]+/u)
    .filter(Boolean)
    .map((word) =>
      word === "openai"
        ? "OpenAI"
        : word === "ai"
          ? "AI"
          : `${word.charAt(0).toUpperCase()}${word.slice(1)}`
    )
    .join(" ");
}

export function liveModelDisplayName(
  provider: string,
  model: string
): string | undefined {
  return getApprovedLlmModelEntry(provider, model)?.displayName;
}

export function liveAiReadyLabel(provider: string, model: string): string {
  const modelName = liveModelDisplayName(provider, model);
  const providerName = liveProviderDisplayName(provider);
  const selectedName = modelName ?? (providerName === "AI" ? undefined : providerName);

  return `Live AI answers are available${selectedName ? ` · ${selectedName}` : ""}`;
}

export function liveHealthReadyLabel(health: LiveHealthResponse): string {
  const provider = health.llmSettings?.provider ?? health.provider;
  const model = health.llmSettings?.modelId ?? health.model;

  return liveAiReadyLabel(provider, model);
}

export function liveHealthSelectedAiName(
  health: LiveHealthResponse
): string | undefined {
  const provider = health.llmSettings?.provider ?? health.provider;
  const model = health.llmSettings?.modelId ?? health.model;
  const modelName = liveModelDisplayName(provider, model);
  const providerName = liveProviderDisplayName(provider);

  return modelName ?? (providerName === "AI" ? undefined : providerName);
}

export function liveHealthProviderName(health: LiveHealthResponse): string {
  const currentProvider = liveProviderDisplayName(health.provider);

  return currentProvider === "AI"
    ? liveProviderDisplayName(
        health.llmSettings?.provider ??
          health.requestedProvider ??
          health.livePath.requestedProvider
      )
    : currentProvider;
}

export function liveHealthUnavailableDetail(health: LiveHealthResponse): string {
  const providerName = liveHealthProviderName(health);

  return health.livePath.configured
    ? `${providerName} is unavailable right now.`
    : `${providerName} is selected but not configured.`;
}

export interface LiveProviderErrorInfo {
  kind:
    | "unavailable"
    | "timeout"
    | "rate_limited"
    | "auth"
    | "quota"
    | "invalid_request"
    | "malformed_response"
    | "request"
    | "network"
    | "budget_refused";
  code: string;
  retryable: boolean;
  terminal: boolean;
  attempts: number;
  maxAttempts: number;
  exhausted: boolean;
  noMockFallback: boolean;
  providerStatusCode?: number;
}

export interface LiveExtractResponse {
  ok: boolean;
  provider: LiveProviderSummary;
  laneA: {
    candidates: unknown[];
    notes: unknown[];
    requirements: Requirement[];
    edges: unknown[];
    sourceHashes: string[];
  };
}

export interface TenderUploadTargetSummary {
  bucket: string;
  objectPath: string;
  uploadUrl: string;
  uploadMethod: "gcs-resumable";
  headers?: Record<string, string>;
}

export interface TenderCreateUploadSessionResponse {
  ok: boolean;
  upload: TenderUploadTargetSummary;
  pack: TenderPack;
}

export interface TenderLocalUploadCommitResponse {
  ok: boolean;
  objectPath: string;
  objectGeneration: string;
  sizeBytes: number;
  sha256?: string;
  contentType: string;
}

export interface TenderUploadProgress {
  phase: "uploading" | "retrying";
  attempt: number;
  maxAttempts: number;
  offsetBytes: number;
  totalBytes: number;
  status?: number;
  delayMs?: number;
}

export interface TenderUploadOptions {
  onProgress?(progress: TenderUploadProgress): void;
  sleep?(delayMs: number): Promise<void>;
}

const TENDER_UPLOAD_MAX_ATTEMPTS = 3;
const TENDER_UPLOAD_RETRY_DELAYS_MS = [250, 1_000] as const;
const TENDER_UPLOAD_CHUNK_BYTES = 8 * 1024 * 1024;
const TRANSIENT_TENDER_UPLOAD_STATUSES = new Set([502, 503, 504]);

export interface TenderFinalizeUploadResponse {
  ok: boolean;
  pack: TenderPack;
  stageRun?: { runId: string };
  progressEvent?: TenderProgressEvent;
  workerQueued: boolean;
  duplicate?: boolean;
  duplicateOfPackId?: string;
}

export interface TenderCreatePastedTextResponse {
  ok: boolean;
  pack: TenderPack;
  objectRef?: TenderPack["archiveObjectRef"];
  workerQueued: boolean;
  duplicate: boolean;
}

export interface TenderRemoveDocumentResponse {
  ok: boolean;
  removed: boolean;
  retainedForAudit: boolean;
  message?: string;
  pack: TenderPack;
  document: TenderDocumentRegistryRow;
  manifestArtifact?: ManifestArtifact;
  duplicate?: boolean;
}

export interface TenderParseGate {
  canProceed: boolean;
  blockedReasons: string[];
}

export type TenderReplayStage =
  | "manifest"
  | "classification"
  | "parse"
  | "requirement-crawl"
  | "takeoff";

export type TenderCostLedgerStageId =
  | "classification"
  | "parse"
  | "crawl"
  | "takeoff"
  | "feed";

export interface TenderCostLedgerStageReport {
  stage: TenderCostLedgerStageId;
  label: string;
  status: "recorded" | "missing_usage";
  callCount: number;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  estimatedUsd: CostTelemetryCurrencyAmount;
  estimatedGbp: CostTelemetryCurrencyAmount;
  estimateText: string;
  ledgerEntryIds: string[];
  missingUsageReason?: string;
}

export interface TenderCostLedgerReport {
  schemaVersion: "tender-cost-ledger-report.v1";
  packId: string;
  generatedAt: string;
  pricingBasis: CostTelemetryPricingBasis;
  stages: TenderCostLedgerStageReport[];
  total: CostTelemetryRollup;
  estimateDisclaimer: string;
}

export interface TenderPackStatusResponse {
  ok: boolean;
  pack: TenderPack;
  packDocumentStatus?: {
    status:
      | "processing"
      | "processing_with_failures"
      | "ready"
      | "ready_with_failures"
      | "failed"
      | "empty";
    label: string;
    activeDocumentCount: number;
    readyDocumentCount: number;
    failedDocumentCount: number;
  };
  documents: TenderDocumentRegistryRow[];
  manifestArtifact?: ManifestArtifact | null;
  classificationArtifact?: ClassificationArtifact | null;
  requirementRegisterArtifact?: RequirementRegisterArtifact | null;
  requirementCriticArtifact?: RequirementCriticArtifact | null;
  engineFeedRequirements: EngineFeedRequirementRow[];
  takeoffArtifacts?: TenderTakeoffArtifact[];
  takeoffAdapterArtifacts?: TenderTakeoffAdapterArtifact[];
  takeoffReviewEvents?: TenderTakeoffReviewEvent[];
  progressEvents: TenderProgressEvent[];
  overrides: ClassificationOverrideEvent[];
  acceptance: {
    orgId: string;
    projectId: string;
    tenderId: string;
    packId: string;
    artifactId: string;
    acceptedAt: string;
    acceptedBy: { userId: string; displayLabel: string };
    overrideSetVersion: number;
  } | null;
  parseGate: TenderParseGate;
  costSummary?: CostTelemetryBudgetSummary | null;
  costLedgerReport?: TenderCostLedgerReport | null;
  /**
   * Honest tri-state summary of the modern engine stage chain (classify ->
   * takeoff -> projection -> points -> panel), read from the durable
   * engine-events feed. Distinct from `progressEvents`, which is the legacy
   * intake stream and carries no engine stage identity.
   */
  engineChain?: EngineChainSummary;
}

export interface TenderPackEventsResponse {
  ok: boolean;
  events: TenderProgressEvent[];
}

export interface TenderRetryClassificationResponse {
  ok: boolean;
  pack: TenderPack;
  queued: boolean;
  docId?: string;
}

// Mirrors `TakeoffStageSkipReason` in `apps/server/src/tenderIntakeApi.ts` —
// keep in sync by hand; the two apps do not share a package boundary that
// would let this be imported instead.
export type TenderTakeoffStageSkipReason =
  | "storage_unavailable"
  | "provider_not_configured"
  | "classification_not_ready"
  | "schedule_not_ready"
  | "no_qualifying_drawings";

export interface TenderReplayStageResponse {
  ok: boolean;
  stage: TenderReplayStage;
  replay: "queued" | "completed";
  source: "stored-artifacts";
  outcome?:
    | "completed"
    | "failed"
    | "unsupported"
    | "no_values_read"
    | "completed_with_no_values_read";
  code?: string;
  /** Distinct machine-readable skip reason behind an "unsupported" outcome — see `message` for the honest, human-readable form. */
  reason?: TenderTakeoffStageSkipReason;
  message?: string;
  renderedDrawingCount?: number;
  queued?: boolean;
  pack?: TenderPack;
  document?: TenderDocumentRegistryRow;
  manifestArtifact?: ManifestArtifact;
  classificationQueued?: boolean;
  docId?: string;
  parseArtifacts?: unknown[];
  takeoffArtifacts?: unknown[];
  progressEvents?: TenderProgressEvent[];
  /** Apartment types the hosted take-off could have read on this replay. */
  apartmentTypeCount?: number;
  /** Apartment types this replay actually read; lower than the count above when truncated. */
  processedApartmentTypeCount?: number;
  /** Apartment types whose two blind reads produced no candidate field value. */
  noValuesReadApartmentTypeIds?: string[];
  /** True when the replay stopped at its per-call apartment-type limit. */
  truncated?: boolean;
  currentArtifactRevisionId?: string;
  idempotencyKey?: string;
  duplicate?: boolean;
}

export interface TenderClassificationOverrideResponse {
  ok: boolean;
  overrideEvent: ClassificationOverrideEvent;
  pack: TenderPack;
  classificationArtifact: ClassificationArtifact;
}

export interface TenderAcceptClassificationReviewResponse {
  ok: boolean;
  acceptance: NonNullable<TenderPackStatusResponse["acceptance"]>;
  parseGate: TenderParseGate;
}

export interface TenderRequirementCrawlResponse {
  ok: boolean;
  crawlArtifacts: unknown[];
  requirementRegisterArtifact: RequirementRegisterArtifact;
  requirementCriticArtifact: RequirementCriticArtifact;
  engineFeedRequirements: EngineFeedRequirementRow[];
}

export interface TenderRequirementRowsReviewResponse {
  ok: boolean;
  requirementRegisterArtifact: RequirementRegisterArtifact;
  engineFeedRequirements: EngineFeedRequirementRow[];
}

export interface TenderCriticProposalsReviewResponse {
  ok: boolean;
  requirementCriticArtifact: RequirementCriticArtifact;
}

export interface TenderParseGateResponse {
  ok: boolean;
  parseGate: TenderParseGate;
  acceptance: TenderPackStatusResponse["acceptance"];
}

export interface CopilotFactInput {
  id: string;
  kind:
    | "project"
    | "requirement"
    | "note"
    | "product"
    | "rule"
    | "apartment_solution"
    | "point_ledger"
    | "device_mapping"
    | "risk"
    | "scope"
    | "export"
    | "benchmark";
  plainText: string;
  refs?: CopilotObjectRefInput[];
  sourceRefs?: SourceRef[];
  traceRefs?: string[];
  confidence?: "high" | "medium" | "low";
}

export interface CopilotObjectRefInput {
  kind:
    | "project"
    | "requirement"
    | "note"
    | "apartment_type"
    | "solution_item"
    | "controller"
    | "point_ledger"
    | "device_mapping"
    | "risk"
    | "rfi"
    | "export_row"
    | "product"
    | "rule"
    | "benchmark"
    | "snapshot";
  id: string;
  label?: string;
}

export interface CopilotTurnRequest {
  sessionId: string;
  message: string;
  routeContext: {
    routeId: string;
    view:
      | "cockpit"
      | "requirements"
      | "apartments"
      | "risks"
      | "impact"
      | "exports"
      | "product_database"
      | "rules"
      | "unknown";
    label?: string;
  };
  selectedObject?: CopilotObjectRefInput;
  context: {
    orgId?: string;
    projectId?: string;
    selectedRefs?: CopilotObjectRefInput[];
    facts?: CopilotFactInput[];
    sourceRefs?: SourceRef[];
    ruleRefs?: string[];
    productRefs?: string[];
    traceRefs?: string[];
    unresolvedOpenQuestions?: string[];
    staleStateWarnings?: string[];
  };
}

export interface CopilotToolProposal {
  toolName: "notes.applyTypedNote" | "overrides.applyScopedOverride";
  descriptor: {
    name: string;
    purpose: string;
    requiredRole: "viewer" | "editor" | "spec_reviewer" | "admin";
    mode: "read" | "write";
    confirmation: "none" | "policy" | "always";
    canDryRun: boolean;
  };
  input: Record<string, unknown>;
  requiresConfirmation: boolean;
  guard: {
    allowed: boolean;
    zodValidated: boolean;
    confirmBeforeApply: boolean;
    normalised?: string;
  };
}

export interface CopilotTurnResponse {
  ok: boolean;
  provider: LiveProviderSummary;
  turn: {
    sessionId: string;
    turnId: string;
    requiredOutput: "answer" | "action_plan" | "clarification";
    intent: {
      intent: "question" | "action" | "clarify";
      actionKind: string;
      requiresConfirmation: boolean;
      safetyFlags: string[];
      ambiguity: string[];
    };
    groundingPack?: {
      facts?: CopilotFactInput[];
      sourceRefs?: SourceRef[];
      ruleRefs?: string[];
      productRefs?: string[];
      traceRefs?: string[];
      unresolvedOpenQuestions?: string[];
      staleStateWarnings?: string[];
    };
  };
  events: AssistantTurnEvent[];
  answer: string;
  toolProposal?: CopilotToolProposal;
}

export interface PostCopilotTurnOptions {
  /**
   * The route may return a buffered event array today and a streaming
   * transport later. Keeping this callback at the transport boundary lets the
   * Ask dock render each contract event without owning fetch or server-loop
   * policy.
   */
  onEvent?: (event: AssistantTurnEvent) => void | Promise<void>;
}

export async function fetchLiveHealth(): Promise<LiveHealthResponse> {
  return fetchJson<LiveHealthResponse>("/api/health");
}

export async function extractTenderText(input: {
  text: string;
  projectId: string;
  orgId?: string;
  title?: string;
}): Promise<LiveExtractResponse> {
  return postJson<LiveExtractResponse>("/api/extract", input);
}

export async function createTenderUploadSession(input: {
  orgId: string;
  projectId: string;
  tenderId: string;
  packId: string;
  filename: string;
  contentLength: number;
  contentType: string;
  sourceKind?: "single_file" | "multi_file_batch" | "zip_archive";
  idempotencyKey: string;
  packName?: string;
}): Promise<TenderCreateUploadSessionResponse> {
  return postJson<TenderCreateUploadSessionResponse>(
    "/api/tender-intake/upload-sessions",
    input
  );
}

export async function createTenderPastedText(input: {
  orgId: string;
  projectId: string;
  tenderId: string;
  packId: string;
  pastedText: string;
  title: string;
  idempotencyKey: string;
  packName?: string;
}): Promise<TenderCreatePastedTextResponse> {
  return postJson<TenderCreatePastedTextResponse>(
    "/api/tender-intake/pasted-text",
    input
  );
}

export async function uploadTenderArchiveBytes(
  upload: TenderUploadTargetSummary,
  file: File,
  options: TenderUploadOptions = {}
): Promise<TenderLocalUploadCommitResponse> {
  const contentType = file.type || "application/zip";
  const localUpload = upload.uploadUrl.startsWith("/api/");
  const resumableHostedUpload = !localUpload && upload.uploadMethod === "gcs-resumable";
  const baseHeaders: Record<string, string> = localUpload
    ? {
        ...(upload.headers ?? {}),
        "content-type": contentType
      }
    : {
        // The hosted session URL is already authorized. Session-initiation
        // constraints are not upload-request headers and can fail bucket CORS.
        "content-type": contentType
      };
  const computedSha256 = await tenderFileSha256(file);
  const sleep = options.sleep ?? tenderUploadSleep;
  let offset = 0;
  let finalResponse: globalThis.Response | undefined;

  while (offset < file.size || localUpload) {
    let chunkStart = offset;
    let chunkEnd = localUpload
      ? file.size
      : Math.min(chunkStart + TENDER_UPLOAD_CHUNK_BYTES, file.size);
    let lastTransientStatus: number | undefined;
    let lastTransientError: unknown;
    let chunkAdvanced = false;

    for (let attempt = 1; attempt <= TENDER_UPLOAD_MAX_ATTEMPTS; attempt += 1) {
      options.onProgress?.({
        phase: "uploading",
        attempt,
        maxAttempts: TENDER_UPLOAD_MAX_ATTEMPTS,
        offsetBytes: chunkStart,
        totalBytes: file.size
      });

      let response: globalThis.Response | undefined;

      try {
        response = await fetch(upload.uploadUrl, {
          method: "PUT",
          headers: await tenderUploadHeaders({
            baseHeaders,
            localUpload,
            start: chunkStart,
            end: chunkEnd,
            total: file.size,
            resumable: resumableHostedUpload
          }),
          body:
            localUpload || (chunkStart === 0 && chunkEnd === file.size)
              ? file
              : file.slice(chunkStart, chunkEnd)
        });
      } catch (error) {
        if (!isTransientTenderUploadError(error)) {
          throw new Error(apiNetworkErrorMessage(upload.uploadUrl, error), {
            cause: error
          });
        }

        lastTransientError = error;
        if (resumableHostedUpload) {
          offset = await queryTenderUploadOffset(upload.uploadUrl, file.size, offset);
          chunkStart = offset;
          chunkEnd = Math.min(chunkStart + TENDER_UPLOAD_CHUNK_BYTES, file.size);
        }
      }

      if (response?.ok) {
        finalResponse = response;
        offset = file.size;
        break;
      }

      if (response?.status === 308 && resumableHostedUpload) {
        offset = resumableOffsetFromResponse(response, chunkEnd);
        chunkAdvanced = true;
        break;
      }

      const status = response?.status;
      if (status === undefined && lastTransientError === undefined) {
        throw new Error("Tender archive upload did not receive a response.");
      }

      if (status !== undefined && !TRANSIENT_TENDER_UPLOAD_STATUSES.has(status)) {
        throw new Error(tenderUploadHttpErrorMessage(status));
      }

      lastTransientStatus = status;
      if (attempt === TENDER_UPLOAD_MAX_ATTEMPTS) {
        break;
      }

      if (resumableHostedUpload && status !== undefined) {
        offset = await queryTenderUploadOffset(upload.uploadUrl, file.size, offset);
        chunkStart = offset;
        chunkEnd = Math.min(chunkStart + TENDER_UPLOAD_CHUNK_BYTES, file.size);
      }

      const delayMs = TENDER_UPLOAD_RETRY_DELAYS_MS[attempt - 1] ?? 1_000;
      options.onProgress?.({
        phase: "retrying",
        attempt: attempt + 1,
        maxAttempts: TENDER_UPLOAD_MAX_ATTEMPTS,
        offsetBytes: offset,
        totalBytes: file.size,
        ...(status !== undefined ? { status } : {}),
        delayMs
      });
      await sleep(delayMs);
    }

    if (finalResponse) {
      break;
    }

    if (chunkAdvanced) {
      continue;
    }

    if (lastTransientStatus !== undefined) {
      throw new Error(
        tenderUploadTransientFailureMessage(
          lastTransientStatus,
          TENDER_UPLOAD_MAX_ATTEMPTS
        )
      );
    }

    if (lastTransientError !== undefined) {
      throw new Error(tenderUploadNetworkFailureMessage(TENDER_UPLOAD_MAX_ATTEMPTS));
    }
  }

  if (!finalResponse) {
    throw new Error("Tender archive upload completed without an object receipt.");
  }

  const receipt = await parseTenderUploadReceipt(finalResponse);
  const localObjectPath = nonEmptyString(receipt.objectPath);
  const nativeObjectPath = nonEmptyString(receipt.name);
  const localObjectGeneration = nonEmptyString(receipt.objectGeneration);
  const nativeObjectGeneration = nonEmptyString(receipt.generation);
  const localSizeBytes = finiteNonNegativeInteger(receipt.sizeBytes);
  const nativeSizeBytes = finiteNonNegativeInteger(receipt.size);

  if (
    (localObjectPath && nativeObjectPath && localObjectPath !== nativeObjectPath) ||
    (localObjectGeneration &&
      nativeObjectGeneration &&
      localObjectGeneration !== nativeObjectGeneration) ||
    (localSizeBytes !== undefined &&
      nativeSizeBytes !== undefined &&
      localSizeBytes !== nativeSizeBytes)
  ) {
    throw new Error("Tender archive upload returned a contradictory object receipt.");
  }

  const objectPath = localObjectPath ?? nativeObjectPath;
  const objectGeneration = localObjectGeneration ?? nativeObjectGeneration;
  const sizeBytes = localSizeBytes ?? nativeSizeBytes;

  if (!objectPath || !objectGeneration || sizeBytes === undefined) {
    throw new Error("Tender archive upload returned an incomplete object receipt.");
  }

  if (objectPath !== upload.objectPath) {
    throw new Error("Tender archive upload returned an unexpected object path.");
  }

  if (sizeBytes !== file.size) {
    throw new Error("Tender archive upload returned an unexpected object size.");
  }

  return {
    ok: true,
    objectPath,
    objectGeneration,
    sizeBytes,
    sha256: nonEmptyString(receipt.sha256) ?? computedSha256,
    contentType: nonEmptyString(receipt.contentType) ?? contentType
  };
}

async function tenderUploadHeaders(input: {
  baseHeaders: Record<string, string>;
  localUpload: boolean;
  start: number;
  end: number;
  total: number;
  resumable: boolean;
}): Promise<Record<string, string>> {
  const headers = input.localUpload
    ? Object.fromEntries((await withAuthHeaders(input.baseHeaders)).entries())
    : { ...input.baseHeaders };

  if (input.resumable) {
    headers["content-range"] = `bytes ${input.start}-${input.end - 1}/${input.total}`;
  }

  return headers;
}

async function queryTenderUploadOffset(
  uploadUrl: string,
  totalBytes: number,
  fallbackOffset: number
): Promise<number> {
  try {
    const response = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "content-range": `bytes */${totalBytes}` }
    });

    if (response.status === 308) {
      return resumableOffsetFromResponse(response, fallbackOffset);
    }

    if (response.ok) {
      return totalBytes;
    }
  } catch {
    // The next bounded upload attempt will retry from the last known offset.
  }

  return fallbackOffset;
}

function resumableOffsetFromResponse(
  response: globalThis.Response,
  fallbackOffset: number
): number {
  const range =
    response.headers.get("range") ??
    response.headers.get("x-goog-upload-size-received");
  const match = range?.match(/(?:bytes=)?(?:\d+-)?(\d+)$/u);

  if (!match) {
    return fallbackOffset;
  }

  const lastByte = Number(match[1]);
  return Number.isSafeInteger(lastByte) && lastByte >= 0
    ? lastByte + 1
    : fallbackOffset;
}

function isTransientTenderUploadError(error: unknown): boolean {
  if (error instanceof TypeError) {
    return true;
  }

  if (typeof DOMException !== "undefined" && error instanceof DOMException) {
    return true;
  }

  return (
    error instanceof Error &&
    /aborted|connection|fetch|network|reset|timed? ?out/iu.test(error.message)
  );
}

function tenderUploadHttpErrorMessage(status: number): string {
  return `Tender archive upload was rejected (HTTP ${status}). Check the file or upload session and try again.`;
}

function tenderUploadTransientFailureMessage(status: number, attempts: number): string {
  return `Tender archive upload failed temporarily (HTTP ${status}) after ${attempts.toString()} attempts. Please try again.`;
}

function tenderUploadNetworkFailureMessage(attempts: number): string {
  return `Tender archive upload could not reach storage after ${attempts.toString()} attempts. Please check your connection and try again.`;
}

async function parseTenderUploadReceipt(
  response: globalThis.Response
): Promise<Record<string, unknown>> {
  const contentType = response.headers.get("content-type") ?? "";

  if (!contentType.includes("application/json")) {
    throw new Error(
      "Tender archive upload returned an invalid response. Please try again."
    );
  }

  try {
    return (await response.json()) as Record<string, unknown>;
  } catch {
    throw new Error("Tender archive upload returned invalid JSON. Please try again.");
  }
}

function tenderUploadSleep(delayMs: number): Promise<void> {
  return new Promise((resolve) => globalThis.setTimeout(resolve, delayMs));
}

async function tenderFileSha256(file: File): Promise<string> {
  const hasher = sha256.create();

  if (typeof file.stream !== "function") {
    hasher.update(new Uint8Array(await file.arrayBuffer()));
    return `sha256:${bytesToHex(hasher.digest())}`;
  }

  const reader = file.stream().getReader();

  try {
    while (true) {
      const chunk = await reader.read();

      if (chunk.done) {
        break;
      }

      hasher.update(chunk.value);
    }
  } finally {
    reader.releaseLock();
  }

  return `sha256:${bytesToHex(hasher.digest())}`;
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim().length > 0 ? value : undefined;
}

function finiteNonNegativeInteger(value: unknown): number | undefined {
  const parsed =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim().length > 0
        ? Number(value)
        : Number.NaN;

  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : undefined;
}

export async function finalizeTenderUpload(input: {
  orgId: string;
  projectId: string;
  tenderId: string;
  packId: string;
  objectPath: string;
  objectGeneration: string;
  sizeBytes: number;
  sha256?: string;
  contentType?: string;
  idempotencyKey: string;
  expectedPreviousArtifactRevisionId: string;
}): Promise<TenderFinalizeUploadResponse> {
  return postJson<TenderFinalizeUploadResponse>(
    "/api/tender-intake/upload-finalize",
    input
  );
}

export async function fetchTenderPackStatus(
  packId: string
): Promise<TenderPackStatusResponse> {
  return fetchJson<TenderPackStatusResponse>(
    `/api/tender-intake/packs/${encodeURIComponent(packId)}`
  );
}

export async function fetchTenderPackEvents(
  packId: string
): Promise<TenderPackEventsResponse> {
  return fetchJson<TenderPackEventsResponse>(
    `/api/tender-intake/packs/${encodeURIComponent(packId)}/events`
  );
}

export async function retryTenderClassification(input: {
  orgId: string;
  projectId: string;
  tenderId: string;
  packId: string;
  docId?: string;
}): Promise<TenderRetryClassificationResponse> {
  const { packId, ...body } = input;

  return postJson<TenderRetryClassificationResponse>(
    `/api/tender-intake/packs/${encodeURIComponent(packId)}/retry-classification`,
    body
  );
}

export async function removeTenderDocument(input: {
  orgId: string;
  projectId: string;
  tenderId: string;
  packId: string;
  docId: string;
  expectedArtifactRevisionId: string;
  idempotencyKey: string;
  reason?: string;
}): Promise<TenderRemoveDocumentResponse> {
  const { packId, docId, ...body } = input;

  return postJson<TenderRemoveDocumentResponse>(
    `/api/tender-intake/packs/${encodeURIComponent(packId)}/documents/${encodeURIComponent(docId)}/remove`,
    body
  );
}

export async function replayTenderStage(input: {
  orgId: string;
  projectId: string;
  tenderId: string;
  packId: string;
  stage: TenderReplayStage;
  docId?: string;
  expectedArtifactRevisionId?: string;
  idempotencyKey?: string;
}): Promise<TenderReplayStageResponse> {
  const { packId, ...body } = input;

  return postJson<TenderReplayStageResponse>(
    `/api/tender-intake/packs/${encodeURIComponent(packId)}/replay`,
    body
  );
}

export async function saveTenderClassificationOverride(input: {
  orgId: string;
  projectId: string;
  tenderId: string;
  packId: string;
  docId: string;
  newDecision: "FORCE_INCLUDE" | "FORCE_EXCLUDE" | "CLEAR_OVERRIDE";
  reason: string;
  userId?: string;
  displayLabel?: string;
}): Promise<TenderClassificationOverrideResponse> {
  const { packId, ...body } = input;

  return postJson<TenderClassificationOverrideResponse>(
    `/api/tender-intake/packs/${encodeURIComponent(packId)}/classification-overrides`,
    body
  );
}

export async function acceptTenderClassificationReview(input: {
  orgId: string;
  projectId: string;
  tenderId: string;
  packId: string;
  userId?: string;
  displayLabel?: string;
}): Promise<TenderAcceptClassificationReviewResponse> {
  const { packId, ...body } = input;

  return postJson<TenderAcceptClassificationReviewResponse>(
    `/api/tender-intake/packs/${encodeURIComponent(packId)}/classification-review/accept`,
    body
  );
}

export async function runTenderRequirementCrawl(input: {
  orgId: string;
  projectId: string;
  tenderId: string;
  packId: string;
}): Promise<TenderRequirementCrawlResponse> {
  const { packId, ...body } = input;

  return postJson<TenderRequirementCrawlResponse>(
    `/api/tender-intake/packs/${encodeURIComponent(packId)}/requirement-crawl`,
    body
  );
}

export async function reviewTenderRequirementRows(input: {
  orgId: string;
  projectId: string;
  tenderId: string;
  packId: string;
  rowIds: string[];
  status: "proposed" | "accepted" | "rejected";
  reason?: string;
  userId?: string;
  displayLabel?: string;
}): Promise<TenderRequirementRowsReviewResponse> {
  const { packId, ...body } = input;

  return postJson<TenderRequirementRowsReviewResponse>(
    `/api/tender-intake/packs/${encodeURIComponent(packId)}/requirements/review`,
    body
  );
}

export async function reviewTenderCriticProposals(input: {
  orgId: string;
  projectId: string;
  tenderId: string;
  packId: string;
  proposalIds: string[];
  status: "accepted" | "rejected";
  reason?: string;
  userId?: string;
  displayLabel?: string;
}): Promise<TenderCriticProposalsReviewResponse> {
  const { packId, ...body } = input;

  return postJson<TenderCriticProposalsReviewResponse>(
    `/api/tender-intake/packs/${encodeURIComponent(packId)}/critic-proposals/review`,
    body
  );
}

export async function fetchTenderParseGate(
  packId: string
): Promise<TenderParseGateResponse> {
  return fetchJson<TenderParseGateResponse>(
    `/api/tender-intake/packs/${encodeURIComponent(packId)}/parse-gate`
  );
}

export async function postCopilotTurn(
  input: CopilotTurnRequest,
  options: PostCopilotTurnOptions = {}
): Promise<CopilotTurnResponse> {
  const response = await postJson<CopilotTurnResponse>("/api/copilot", input);

  for (const event of response.events) {
    await options.onEvent?.(event);
  }

  return response;
}

export function apiNetworkErrorMessage(path: string, error: unknown): string {
  const detail =
    error instanceof Error && error.message.trim()
      ? error.message.trim()
      : "The request did not receive a response.";
  const hint = path.startsWith("/api/")
    ? [
        "Check that the API server is running on the configured local port",
        "and that the Vite /api proxy is forwarding requests."
      ].join(" ")
    : "Check that the API server is reachable and allows requests from this page.";

  return `Network error while calling ${path}: ${detail}. ${hint}`;
}

async function postJson<Payload>(path: string, body: unknown): Promise<Payload> {
  let response: globalThis.Response;

  try {
    response = await fetch(path, {
      method: "POST",
      headers: await withAuthHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(body)
    });
  } catch (error) {
    throw new Error(apiNetworkErrorMessage(path, error), { cause: error });
  }

  return parseJsonResponse<Payload>(response, path);
}

async function fetchJson<Payload>(path: string): Promise<Payload> {
  let response: globalThis.Response;

  try {
    response = await fetch(path, {
      headers: await withAuthHeaders({ accept: "application/json" })
    });
  } catch (error) {
    throw new Error(apiNetworkErrorMessage(path, error), { cause: error });
  }

  return parseJsonResponse<Payload>(response, path);
}

async function parseJsonResponse<Payload>(
  response: globalThis.Response,
  path: string
): Promise<Payload> {
  const contentType = response.headers.get("content-type") ?? "";

  if (!contentType.includes("application/json")) {
    const text = await response.text();

    if (!response.ok) {
      throw new Error(text.trim() || `Request failed with ${response.status}`);
    }

    throw new Error("Live lane endpoint returned a non-JSON response.");
  }

  const payload = (await response.json()) as Payload & {
    error?: string;
    code?: string;
    liveProviderError?: LiveProviderErrorInfo;
  };

  if (!response.ok) {
    throw new Error(apiResponseErrorMessage(path, payload, response.status));
  }

  return payload;
}

function apiResponseErrorMessage(
  path: string,
  payload: { error?: string; code?: string; liveProviderError?: LiveProviderErrorInfo },
  status: number
): string {
  if (path.endsWith("/api/copilot") && payload.liveProviderError) {
    return copilotFailureMessage(payload.liveProviderError, payload.code);
  }

  return payload.error ?? `Request failed with ${status}`;
}

export function copilotFailureMessage(
  error: LiveProviderErrorInfo,
  serverCode = error.code
): string {
  if (error.kind === "budget_refused" && serverCode === "LLM_UNMETERED_FAILURE_CAP") {
    return [
      "The co-pilot answer path is paused after repeated failed answers.",
      "Try again shortly, or ask someone technical to check the live service."
    ].join(" ");
  }

  return COPILOT_FAILURE_MESSAGES[error.kind];
}

const COPILOT_FAILURE_MESSAGES: Record<LiveProviderErrorInfo["kind"], string> = {
  timeout: "The co-pilot took too long to answer. Try again with a shorter question.",
  rate_limited: "The co-pilot is busy right now. Try again in a moment.",
  malformed_response:
    "The co-pilot could not produce a usable answer. Try again; if it repeats, keep working from the project screens while the service is checked.",
  network: "The co-pilot service did not complete the request. Try again in a moment.",
  invalid_request:
    "The co-pilot could not accept the request. Keep working from the project screens while the service is checked.",
  request:
    "The co-pilot could not process that request. Try rephrasing; if it repeats, keep working from the project screens while the service is checked.",
  auth: "The co-pilot is unavailable right now. Keep working from the project screens and ask someone technical to check the service.",
  quota:
    "The co-pilot is unavailable right now. Keep working from the project screens and ask someone technical to check the service.",
  unavailable:
    "The co-pilot is unavailable right now. Keep working from the project screens and ask someone technical to check the service.",
  budget_refused:
    "The co-pilot is unavailable right now. Keep working from the project screens and ask someone technical to check the service."
};
